// ─── PageHeader canvas component (enterprise upgrade) ────────────────────────

import React from 'react';

// ─── STATUS BADGE COLOURS ─────────────────────────────────────────────────────
const STATUS_COLORS = {
  success: { bg: '#D1FAE5', color: '#065F46' },
  warning: { bg: '#FEF3C7', color: '#92400E' },
  danger:  { bg: '#FEE2E2', color: '#991B1B' },
  info:    { bg: '#DBEAFE', color: '#1E40AF' },
  neutral: { bg: '#F3F4F6', color: '#374151' },
};

// ─── BUTTON SIZE MAP ──────────────────────────────────────────────────────────
const BTN_SIZE = {
  sm: { fontSize: 11, fontWeight: 500, padH: 10, padV: 5, borderRadius: 5 },
  md: { fontSize: 13, fontWeight: 600, padH: 14, padV: 7, borderRadius: 7 },
  lg: { fontSize: 15, fontWeight: 600, padH: 20, padV: 10, borderRadius: 8 },
};

// ─── BUTTON VARIANT BASE STYLES ───────────────────────────────────────────────
function variantStyle(variant) {
  if (variant === 'primary')   return { bg: '#2563EB', color: '#fff', border: 'transparent' };
  if (variant === 'danger')    return { bg: '#DC2626', color: '#fff', border: 'transparent' };
  return { bg: '#fff', color: '#111827', border: '#CDD0D8' }; // secondary
}

// ─── SPINNER ─────────────────────────────────────────────────────────────────
function Spinner({ color }) {
  return (
    <span style={{
      display: 'inline-block', width: 12, height: 12,
      border: `2px solid ${color}44`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'ph-spin .7s linear infinite',
      flexShrink: 0,
    }} />
  );
}
function normalize(comp) {
  return {
    showIcon: false,
    showBack: false,
    showBreadcrumbs: false,
    showStatus: false,
    showTabs: false,
    showMeta: false,
    sticky: false,
    compact: false,
    titleAlign: 'left',
    breadcrumbs: [],
    tabs: [],
    actions: [],
    breadcrumbSeparator: '/',
    breadcrumbFontSize: 12,
    breadcrumbColor: '#6B7280',
    breadcrumbActiveColor: '#111827',
    statusVariant: 'warning',
    statusLabel: 'Draft',
    statusPosition: 'inline',
    statusFontSize: 11,
    statusPadH: 10,
    statusPadV: 3,
    statusRadius: 20,
    tabStyle: 'underline',
    tabActiveColor: '#2563EB',
    tabInactiveColor: '#6B7280',
    tabBarBg: 'transparent',
    tabFontSize: 13,
    tabFontWeight: 500,
    metaFontSize: 12,
    metaColor: '#9CA3AF',
    iconType: 'emoji',
    iconEmoji: '📋',
    iconSize: 32,
    iconBg: 'transparent',
    iconRadius: 8,
    titleAreaGap: 10,
    paddingV: 16,
    paddingH: 0,
    ...comp,   // comp values win over defaults
  };
}
// ─── ACTION BUTTON ────────────────────────────────────────────────────────────
function ActionButton({ action, comp }) {
  if (action.visible === false) return null;

  const sz  = BTN_SIZE[action.size || 'md'];
  const vs  = variantStyle(action.variant);
  const dis = action.disabled || action.loading;
  const innerGap = comp.actionInnerGap ?? 5;
  const padH = comp.actionPadH === '' || comp.actionPadH == null ? sz.padH : comp.actionPadH;
  const padV = comp.actionPadV === '' || comp.actionPadV == null ? sz.padV : comp.actionPadV;
  const borderRadius = comp.actionRadius === '' || comp.actionRadius == null ? sz.borderRadius : comp.actionRadius;

  return (
    <button
      title={action.disabled && action.disabledReason ? action.disabledReason : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: innerGap,
        padding: `${padV}px ${padH}px`,
        fontSize: sz.fontSize, fontWeight: sz.fontWeight,
        borderRadius,
        background: dis ? '#F3F4F6' : vs.bg,
        color: dis ? '#9CA3AF' : vs.color,
        border: `1px solid ${dis ? '#E2E5EA' : vs.border}`,
        cursor: dis ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        opacity: action.disabled && !action.loading ? 0.6 : 1,
        transition: 'background .15s, box-shadow .15s',
        whiteSpace: 'nowrap',
        outline: 'none',
      }}
    >
      {action.loading ? (
        <Spinner color={dis ? '#9CA3AF' : vs.color} />
      ) : (
        action.icon && <span style={{ fontSize: sz.fontSize + 1 }}>{action.icon}</span>
      )}
      {action.label}
    </button>
  );
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ comp }) {
  const { statusVariant, statusLabel, statusCustomBg, statusCustomColor,
          statusFontSize = 11, statusPadH = 10, statusPadV = 3, statusRadius = 20 } = comp;

  const palette = statusVariant === 'custom'
    ? { bg: statusCustomBg || '#E5E7EB', color: statusCustomColor || '#374151' }
    : STATUS_COLORS[statusVariant] || STATUS_COLORS.neutral;

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: palette.bg, color: palette.color,
      fontSize: statusFontSize, fontWeight: 600,
      padding: `${statusPadV}px ${statusPadH}px`,
      borderRadius: statusRadius,
      letterSpacing: '.01em',
      flexShrink: 0,
    }}>
      {statusLabel}
    </span>
  );
}

// ─── BREADCRUMBS ─────────────────────────────────────────────────────────────
function Breadcrumbs({ comp }) {
  const {
    breadcrumbs = [], breadcrumbSeparator = '/',
    breadcrumbFontSize = 12, breadcrumbColor = '#6B7280',
    breadcrumbActiveColor = '#111827',
  } = comp;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap',
      gap: 4, fontSize: breadcrumbFontSize,
      marginBottom: 4,
    }}>
      {breadcrumbs.map((crumb, i) => {
        const isLast = i === breadcrumbs.length - 1;
        return (
          <React.Fragment key={crumb.id || i}>
            <span style={{
              color: isLast ? breadcrumbActiveColor : breadcrumbColor,
              fontWeight: isLast ? 500 : 400,
              cursor: crumb.href && !isLast ? 'pointer' : 'default',
            }}>
              {crumb.label}
            </span>
            {!isLast && (
              <span style={{ color: breadcrumbColor, opacity: 0.5, fontSize: breadcrumbFontSize - 1 }}>
                {breadcrumbSeparator}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── TAB BAR ─────────────────────────────────────────────────────────────────
function TabBar({ comp }) {
  const {
    tabs = [], activeTab, tabStyle = 'underline',
    tabActiveColor = '#2563EB', tabInactiveColor = '#6B7280',
    tabBarBg = 'transparent',
    tabFontSize = 13, tabFontWeight = 500,
  } = comp;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end',
      background: tabBarBg,
      gap: tabStyle === 'pill' ? 4 : 0,
      marginTop: 2,
      borderBottom: tabStyle === 'underline' ? '1px solid #E2E5EA' : 'none',
    }}>
      {tabs.map((tab) => {
        const active = tab.key === activeTab;
        const baseTabStyle = {
          display: 'inline-flex', alignItems: 'center', gap: 5,
          fontSize: tabFontSize, fontWeight: active ? 600 : tabFontWeight,
          cursor: 'pointer', userSelect: 'none',
          transition: 'all .15s',
          color: active ? tabActiveColor : tabInactiveColor,
        };

        // underline style
        if (tabStyle === 'underline') return (
          <div key={tab.key} style={{
            ...baseTabStyle,
            padding: '8px 14px',
            borderBottom: active ? `2px solid ${tabActiveColor}` : '2px solid transparent',
            marginBottom: -1,
          }}>
            {tab.icon && <span>{tab.icon}</span>}
            {tab.label}
          </div>
        );

        // filled style
        if (tabStyle === 'filled') return (
          <div key={tab.key} style={{
            ...baseTabStyle,
            padding: '8px 16px',
            background: active ? tabActiveColor : 'transparent',
            color: active ? '#fff' : tabInactiveColor,
            borderRadius: '6px 6px 0 0',
          }}>
            {tab.icon && <span>{tab.icon}</span>}
            {tab.label}
          </div>
        );

        // pill style
        return (
          <div key={tab.key} style={{
            ...baseTabStyle,
            padding: '5px 14px',
            background: active ? `${tabActiveColor}15` : 'transparent',
            borderRadius: 20,
            border: active ? `1px solid ${tabActiveColor}40` : '1px solid transparent',
          }}>
            {tab.icon && <span>{tab.icon}</span>}
            {tab.label}
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN CANVAS COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
// ✅ fix
export default function PageHeader({ comp: rawComp }) {
  const comp = normalize(rawComp);
  const {
    title, subtitle,
    titleFontSize = 22, titleFontWeight = 700, titleColor = '#111827',
    titleAlign = 'left', titleMaxWidth = '', titleWrap = false, titleLineClamp = 1,
    titleLineHeight = 1.2, titleLetterSpacing = 0,
    titleItalic = false, titleUnderline = false, titleTransform = 'none',
    showSubtitle = true,
    subtitleAlign = 'left', subtitleMaxWidth = '', subtitleWrap = true, subtitleLineClamp = 2,
    subtitleFontSize = 14, subtitleFontWeight = 400, subtitleColor = '#6B7280',
    subtitleLineHeight = 1.4, subtitleLetterSpacing = 0,
    subtitleItalic = false, subtitleUnderline = false, subtitleTransform = 'none',
    titleAreaGap = 10,
    showIcon = false, iconType = 'emoji', iconEmoji = '📋', iconUrl = '',
    iconSize = 32, iconBg = 'transparent', iconRadius = 8,
    showBack = false, backLabel = 'Back', backIcon = '←',
    showBreadcrumbs = false,
    showStatus = false, statusPosition = 'inline',
    showTabs = false,
    showMeta = false, metaText = '', metaFontSize = 12, metaColor = '#9CA3AF',
    actionGap = 6, actionInnerGap = 5, actionPadH = '', actionPadV = '', actionRadius = '',
    sticky = false, compact = false, customHeight = '',
    paddingV = 16, paddingH = 0,
    bg = 'transparent', borderBottom = true, borderColor = '#E2E5EA',
    actions = [],
  } = comp;
  const effectivePadV = compact ? 10 : paddingV;
  const titleJustify = titleAlign === 'center'
    ? 'center'
    : titleAlign === 'right'
      ? 'flex-end'
      : 'flex-start';
  const titleTextAlign = titleAlign;
  const subtitleTextAlign = subtitleAlign;
  const resolveWidth = (value) => {
    if (value === '' || value == null) return undefined;
    return /^\d+$/.test(String(value).trim()) ? `${String(value).trim()}px` : value;
  };
  const titleResolvedMaxWidth = resolveWidth(titleMaxWidth);
  const subtitleResolvedMaxWidth = resolveWidth(subtitleMaxWidth);
  const getAlignedBlockMargins = (align) => ({
    marginLeft: align === 'right' ? 'auto' : undefined,
    marginRight: align === 'left' ? 'auto' : undefined,
  });

  // Split actions by position
  const leftActions  = actions.filter((a) => a.position === 'left'  && a.visible !== false);
  const rightActions = actions.filter((a) => a.position !== 'left'  && a.visible !== false);

  return (
    <>
      {/* Keyframe for spinner */}
      <style>{`@keyframes ph-spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{
        background: bg,
        borderBottom: borderBottom ? `1px solid ${borderColor}` : 'none',
        padding: `${effectivePadV}px ${paddingH}px`,
        height: customHeight ? `${customHeight}px` : 'auto',
        position: sticky ? 'sticky' : 'relative',
        top: sticky ? 0 : undefined,
        zIndex: sticky ? 10 : undefined,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}>

        {/* ── BACK BUTTON ────────────────────────────────────────────── */}
        {showBack && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 12, fontWeight: 500, color: '#6B7280',
            cursor: 'pointer', marginBottom: 6,
            width: 'fit-content',
          }}>
            <span>{backIcon}</span>
            <span>{backLabel}</span>
          </div>
        )}

        {/* ── BREADCRUMBS ─────────────────────────────────────────────── */}
        {showBreadcrumbs && <Breadcrumbs comp={comp} />}

        {/* ── STATUS (top row positions) ──────────────────────────────── */}
        {showStatus && ['top-left', 'top-right'].includes(comp.statusPosition) && (
          <div style={{
            display: 'flex',
            justifyContent: comp.statusPosition === 'top-right' ? 'flex-end' : 'flex-start',
            marginBottom: 6,
          }}>
            <StatusBadge comp={comp} />
          </div>
        )}

        {/* ── MAIN HEADER ROW ─────────────────────────────────────────── */}


<div style={{
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
}}>

          {/* Left actions (if any) */}
          {leftActions.length > 0 && (
            <div style={{ display: 'flex', gap: actionGap, flexShrink: 0 }}>
              {leftActions.map((a, i) => <ActionButton key={a.id || i} action={a} comp={comp} />)}
            </div>
          )}

          {/* Title area: icon + title block */}
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: titleAreaGap, flex: 1, minWidth: 0,
            justifyContent: titleJustify,
          }}>
            {/* Icon slot */}
            {showIcon && (
              <div style={{
                width: iconSize, height: iconSize, borderRadius: iconRadius,
                background: iconBg, display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexShrink: 0, overflow: 'hidden',
              }}>
                {iconType === 'url' && iconUrl
                  ? <img src={iconUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <span style={{ fontSize: iconSize * 0.6 }}>{iconEmoji}</span>
                }
              </div>
            )}

            {/* Title + subtitle + inline status */}
            <div style={{
              minWidth: 0,
              flex: titleAlign === 'left' ? '0 1 auto' : 1,
              textAlign: titleTextAlign,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                gap: 8,
                justifyContent: titleJustify,
              }}>
                <h1 style={{
                  margin: 0, padding: 0,
                  fontSize: titleFontSize, fontWeight: titleFontWeight,
                  color: titleColor,
                  lineHeight: titleLineHeight,
                  letterSpacing: titleLetterSpacing,
                  fontStyle: titleItalic ? 'italic' : 'normal',
                  textDecoration: titleUnderline ? 'underline' : 'none',
                  textTransform: titleTransform,
                  maxWidth: titleResolvedMaxWidth,
                  ...getAlignedBlockMargins(titleAlign),
                  ...(titleWrap
                    ? {
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: titleLineClamp || 2,
                    }
                    : {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }),
                }}>
                  {title}
                </h1>
                {/* Inline status badge */}
                {showStatus && statusPosition === 'inline' && (
                  <StatusBadge comp={comp} />
                )}
              </div>

              {showStatus && statusPosition === 'below-title' && (
                <div style={{ marginTop: 6, ...getAlignedBlockMargins(titleAlign), width: 'fit-content' }}>
                  <StatusBadge comp={comp} />
                </div>
              )}

              {showSubtitle && subtitle && (
                <p style={{
                  margin: '3px 0 0', padding: 0,
                  fontSize: subtitleFontSize, fontWeight: subtitleFontWeight,
                  color: subtitleColor,
                  textAlign: subtitleTextAlign,
                  lineHeight: subtitleLineHeight,
                  letterSpacing: subtitleLetterSpacing,
                  fontStyle: subtitleItalic ? 'italic' : 'normal',
                  textDecoration: subtitleUnderline ? 'underline' : 'none',
                  textTransform: subtitleTransform,
                  maxWidth: subtitleResolvedMaxWidth,
                  ...getAlignedBlockMargins(subtitleAlign),
                  ...(subtitleWrap
                    ? {
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: subtitleLineClamp || 2,
                    }
                    : {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }),
                }}>
                  {subtitle}
                </p>
              )}

              {showStatus && statusPosition === 'below-subtitle' && (
                <div style={{ marginTop: 6, ...getAlignedBlockMargins(subtitleAlign), width: 'fit-content' }}>
                  <StatusBadge comp={comp} />
                </div>
              )}
            </div>
          </div>

          {/* Right side: meta + right actions */}
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: 10, flexShrink: 0,
          }}>
            {showMeta && metaText && (
              <span style={{
                fontSize: metaFontSize, color: metaColor,
                whiteSpace: 'nowrap',
              }}>
                {metaText}
              </span>
            )}
            {showStatus && statusPosition === 'right' && (
              <StatusBadge comp={comp} />
            )}
            {rightActions.length > 0 && (
              <div style={{ display: 'flex', gap: actionGap }}>
                {rightActions.map((a, i) => <ActionButton key={a.id || i} action={a} comp={comp} />)}
              </div>
            )}
          </div>
        </div>

        {/* ── TAB BAR ─────────────────────────────────────────────────── */}
        {showTabs && (
          <div style={{ marginTop: 12 }}>
            <TabBar comp={comp} />
          </div>
        )}
      </div>
    </>
  );
}
